import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, InlineError, Select, Skeleton } from '../../components/ui';
import { changeLine, filterLog, type ChangeRow, type LogFilters } from './changeLog';
import type { ScoreV3Api } from './scoreboardV3Api';
import {
  enoughHistory,
  lastFullChange,
  trendBuckets,
  type TrendBucket,
  type TrendMetric,
  type TrendUnit,
} from './trends';
import './scoreboardV3.css';

const OPEN: LogFilters = { section: '', who: '', period: '7d', editsOnly: false, metric: '' };

export function ChangeLogTab({
  api,
  metric,
  onClearMetric,
}: {
  api: ScoreV3Api;
  metric: string;
  onClearMetric: () => void;
}) {
  const [filters, setFilters] = useState<LogFilters>(OPEN);
  const log = useQuery({
    queryKey: ['marketing-score-change-log'],
    queryFn: () => api.changeLog(),
    retry: false,
  });
  const rows = log.data;
  const shown = useMemo(
    () => (rows ? filterLog(rows, { ...filters, metric }) : []),
    [rows, filters, metric],
  );
  if (log.isPending) return <Skeleton lines={8} label="Loading Change log" />;
  if (log.isError)
    return <InlineError>The Change log could not load. Reload to try again.</InlineError>;
  const lines = shown.map((row) => ({ row, line: changeLine(row) }));
  const sections = [...new Set((rows ?? []).map((r) => r.section))];
  const people = [...new Set((rows ?? []).map((r) => changeLine(r).who))];
  const set = (change: Partial<LogFilters>) => setFilters({ ...filters, ...change });
  const metricName = metric ? (rows ?? []).find((r) => r.metric_id === metric)?.metric : null;
  return (
    <div className="score-stack">
      <div className="score-filters">
        <Select
          label="Section"
          value={filters.section}
          options={[
            { value: '', label: 'All sections' },
            ...sections.map((s) => ({ value: s, label: s })),
          ]}
          onChange={(e) => set({ section: e.target.value })}
        />
        <Select
          label="Who"
          value={filters.who}
          options={[
            { value: '', label: 'Everyone' },
            ...people.map((p) => ({ value: p, label: p })),
          ]}
          onChange={(e) => set({ who: e.target.value })}
        />
        <Select
          label="When"
          value={filters.period}
          options={[
            { value: '7d', label: 'Last 7 days' },
            { value: '30d', label: 'Last 30 days' },
            { value: 'month', label: 'This month' },
            { value: 'all', label: 'All time' },
          ]}
          onChange={(e) => set({ period: e.target.value as LogFilters['period'] })}
        />
        <div className="score-segment" role="group" aria-label="Show">
          <Button
            variant={filters.editsOnly ? 'ghost' : 'secondary'}
            aria-pressed={!filters.editsOnly}
            onClick={() => set({ editsOnly: false })}
          >
            Every save
          </Button>
          <Button
            variant={filters.editsOnly ? 'secondary' : 'ghost'}
            aria-pressed={filters.editsOnly}
            onClick={() => set({ editsOnly: true })}
          >
            Edits only
          </Button>
        </div>
      </div>
      {metricName ? (
        <p role="status">
          Showing changes to <strong>{metricName}</strong>.{' '}
          <Button variant="ghost" onClick={onClearMetric}>
            Show all
          </Button>
        </p>
      ) : null}
      {lines.length === 0 ? (
        <section className="score-card score-empty-state">
          <h3>No changes in this period</h3>
          <p className="score-muted">
            Every time someone enters or changes a number on the Board, it shows up here with who
            did it, the old and new value, and when.
          </p>
        </section>
      ) : (
        <section className="score-card">
          <header className="score-card-head">
            <div>
              <strong>
                {lines.length} {lines.length === 1 ? 'change' : 'changes'}
              </strong>
              <small>
                Times are Manila. &ldquo;For&rdquo; is the day the number belongs to. Nothing here
                can be edited or deleted.
              </small>
            </div>
          </header>
          <div className="score-table-scroll" role="region" aria-label="Change log" tabIndex={0}>
            <table className="score-table score-log-table">
              <thead>
                <tr>
                  <th scope="col">When</th>
                  <th scope="col">Who</th>
                  <th scope="col">Number</th>
                  <th scope="col">For day</th>
                  <th scope="col">Old to new</th>
                  <th scope="col">How</th>
                </tr>
              </thead>
              <tbody>
                {lines.map(({ row, line }, index) => (
                  <tr key={`${row.changed_at}-${row.metric_id}-${String(index)}`}>
                    <td className="score-log-when">{line.when}</td>
                    <td className="score-log-who">{line.who}</td>
                    <td className="score-log-metric">{line.metric}</td>
                    <td className="score-log-day">{line.day}</td>
                    <td className="score-log-change">
                      {line.from !== '' || line.to !== '' ? (
                        <>
                          <s>{line.from}</s> <span aria-label="to">&rarr;</span>{' '}
                          <strong>{line.to}</strong>{' '}
                        </>
                      ) : null}
                      <small>{line.label}</small>
                    </td>
                    <td className="score-log-how">{line.how}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function bucketLabel(start: string, unit: TrendUnit): string {
  const month = MONTHS[Number(start.slice(5, 7)) - 1] ?? '';
  return unit === 'month' ? month : `${String(Number(start.slice(8, 10)))} ${month}`;
}
function show(value: number | null, metric: TrendMetric): string {
  if (value === null) return 'none';
  if (metric.value_type === 'rate' && metric.rate_format !== 'money')
    return `${(value * 100).toFixed(1)}%`;
  if (metric.value_type === 'money' || metric.rate_format === 'money')
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value);
}

function Chart({
  buckets,
  metric,
  unit,
}: {
  buckets: TrendBucket[];
  metric: TrendMetric;
  unit: TrendUnit;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const W = 720;
  const H = 240;
  const L = 8;
  const B = 32;
  const T = 20;
  const max = Math.max(1e-9, ...buckets.map((b) => b.value ?? 0)) * 1.1;
  const bw = (W - L) / buckets.length;
  const tip = hover === null ? null : buckets[hover];
  return (
    <div className="score-chart">
      <svg
        viewBox={`0 0 ${String(W)} ${String(H)}`}
        role="img"
        aria-label={`${metric.label} by ${unit}`}
      >
        <defs>
          <pattern
            id="score-hatch"
            width="5"
            height="5"
            patternTransform="rotate(135)"
            patternUnits="userSpaceOnUse"
          >
            <rect width="2" height="5" className="score-bar-fill" />
          </pattern>
        </defs>
        {buckets.map((b, i) => {
          const height = b.value === null ? 0 : ((H - T - B) * b.value) / max;
          return (
            <g key={b.start}>
              <rect
                className={b.partial ? 'score-bar score-bar--partial' : 'score-bar'}
                x={L + i * bw + bw * 0.18}
                y={H - B - height}
                width={bw * 0.64}
                height={height}
                fill={b.partial ? 'url(#score-hatch)' : undefined}
              />
              <rect
                className="score-bar-hit"
                x={L + i * bw}
                y={T}
                width={bw}
                height={H - T - B}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
              />
              <text
                className="score-axis"
                x={L + i * bw + bw / 2}
                y={H - B + 18}
                textAnchor="middle"
              >
                {bucketLabel(b.start, unit)}
              </text>
            </g>
          );
        })}
        <line className="score-axis-line" x1={L} x2={W} y1={H - B} y2={H - B} />
      </svg>
      {tip ? (
        <div className="score-tip" role="status">
          <strong>
            {unit === 'week' ? 'Week of ' : ''}
            {bucketLabel(tip.start, unit)}
          </strong>
          <br />
          {metric.label}: {show(tip.value, metric)}
          <br />
          {tip.days} of {tip.of} days entered
        </div>
      ) : null}
    </div>
  );
}

export function TrendsTab({
  api,
  onBoard,
  onChanges,
}: {
  api: ScoreV3Api;
  onBoard: () => void;
  onChanges: (metricId: string) => void;
}) {
  const [section, setSection] = useState('');
  const [unit, setUnit] = useState<TrendUnit>('week');
  const [metricId, setMetricId] = useState('');
  const sections = useQuery({
    queryKey: ['marketing-score-sections'],
    queryFn: () => api.sections(),
    retry: false,
  });
  const chosen = section || sections.data?.[0]?.id || '';
  const trend = useQuery({
    queryKey: ['marketing-score-trend', chosen, unit],
    queryFn: () => api.trend(chosen, unit),
    enabled: chosen !== '',
    retry: false,
  });
  if (sections.isPending || (chosen !== '' && trend.isPending))
    return <Skeleton lines={8} label="Loading Trends" />;
  if (sections.isError || trend.isError)
    return <InlineError>Trends could not load. Reload to try again.</InlineError>;
  const payload = trend.data;
  if (!payload) return <p>No sections have been set up yet.</p>;
  const metric = payload.metrics.find((m) => m.id === metricId) ?? payload.metrics[0];
  const toolbar = (
    <div className="score-filters">
      <Select
        label="Section"
        value={chosen}
        options={(sections.data ?? []).map((s) => ({ value: s.id, label: s.name }))}
        onChange={(e) => {
          setSection(e.target.value);
          setMetricId('');
        }}
      />
      <div className="score-segment" role="group" aria-label="Group by">
        <Button
          variant={unit === 'week' ? 'secondary' : 'ghost'}
          aria-pressed={unit === 'week'}
          onClick={() => setUnit('week')}
        >
          Weeks
        </Button>
        <Button
          variant={unit === 'month' ? 'secondary' : 'ghost'}
          aria-pressed={unit === 'month'}
          onClick={() => setUnit('month')}
        >
          Months
        </Button>
      </div>
      <small>
        Weeks run Monday to Sunday, Manila time. Rates are worked out from the period&rsquo;s
        totals, not averaged.
      </small>
    </div>
  );
  if (!metric || payload.kind === 'snapshot')
    return (
      <div className="score-stack">
        {toolbar}
        <section className="score-card score-empty-state">
          <h3>Not enough history yet</h3>
          <p className="score-muted">
            Pipeline shows the count on a given day, so it has no weekly total to chart.
          </p>
        </section>
      </div>
    );
  const buckets = trendBuckets(metric, payload, unit);
  if (!enoughHistory(buckets))
    return (
      <div className="score-stack">
        {toolbar}
        <section className="score-card score-empty-state">
          <h3>Not enough history yet</h3>
          <p className="score-muted">
            Trends need at least two {unit === 'week' ? 'weeks' : 'months'} of entries. Keep
            entering daily numbers on the Board and the first bars appear here.
          </p>
          <Button variant="secondary" onClick={onBoard}>
            Go to the Board
          </Button>
        </section>
      </div>
    );
  const change = lastFullChange(buckets);
  const current = buckets[buckets.length - 1];
  return (
    <div className="score-stack">
      {toolbar}
      <div className="score-split score-split--trend">
        <section className="score-card" aria-label={`${metric.label} chart`}>
          <div className="score-headline-row">
            <div>
              <small>{metric.label}</small>
            </div>
            <div>
              <small>Last full {unit}</small>
              <strong className="score-big">{show(change?.last ?? null, metric)}</strong>{' '}
              {change?.percent != null ? (
                <span className={change.percent >= 0 ? 'score-up' : 'score-down'}>
                  {change.percent >= 0 ? '▲' : '▼'} {Math.abs(change.percent).toFixed(0)}%
                </span>
              ) : null}
            </div>
            <div>
              <small>This {unit} so far</small>
              <strong className="score-big score-muted">
                {show(current?.value ?? null, metric)}
              </strong>
            </div>
          </div>
          <Chart buckets={buckets} metric={metric} unit={unit} />
          <p className="score-legend">
            <span className="score-swatch" /> Every day entered{' '}
            <span className="score-swatch score-swatch--partial" /> Some days missing, so the bar
            may be low
          </p>
        </section>
        <section className="score-card" aria-label="Metrics in this section">
          <header className="score-card-head">
            <strong>Metrics</strong>
          </header>
          <ul className="score-list">
            {payload.metrics.map((m) => (
              <li key={m.id}>
                <button
                  type="button"
                  className={
                    m.id === metric.id ? 'score-list-item score-list-item--on' : 'score-list-item'
                  }
                  aria-pressed={m.id === metric.id}
                  onClick={() => setMetricId(m.id)}
                >
                  <strong>{m.label}</strong>
                </button>
              </li>
            ))}
          </ul>
        </section>
      </div>
      <section className="score-card">
        <header className="score-card-head">
          <strong>
            {metric.label}, {unit === 'week' ? 'week by week' : 'month by month'}
          </strong>
          <Button variant="ghost" onClick={() => onChanges(metric.id)}>
            See changes to this number
          </Button>
        </header>
        <div className="score-table-scroll" role="region" aria-label="Trend table" tabIndex={0}>
          <table className="score-table">
            <thead>
              <tr>
                <th scope="col">{unit === 'week' ? 'Week of' : 'Month'}</th>
                <th scope="col">{metric.label}</th>
                <th scope="col">Days in</th>
              </tr>
            </thead>
            <tbody>
              {[...buckets].reverse().map((b) => (
                <tr key={b.start}>
                  <th scope="row">
                    {bucketLabel(b.start, unit)}
                    {b.current ? <small>(so far)</small> : null}
                  </th>
                  <td>{show(b.value, metric)}</td>
                  <td>
                    {b.days} of {b.of}
                    {b.partial ? <small>gaps</small> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

export type { ChangeRow };
